import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Plus, FileText, ArrowLeft, Shield, Paperclip, PenTool, XCircle, CheckCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Contrato = {
  id: string; folio: string; numero_contrato: string; fecha: string; estado: string;
  tipo: string; modalidad: string;
  fecha_inicio: string; fecha_fin: string;
  monto_maximo: number; anticipo_monto: number; anticipo_amortizado: number;
  proveedor_nombre: string; proveedor_rfc: string;
};

export function ContratosPage() {
  const [estado, setEstado] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['contratos', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<Contrato[]>(`/contratos?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones · Contratos</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Contratos</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Instrumentos jurídicos con proveedores: vigencia, garantías, anexos y amortización de anticipo.
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)}><Plus size={16} /> Nuevo contrato</Button>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit">
        {['', 'borrador', 'firmado', 'vigente', 'finiquitado', 'rescindido', 'cancelado'].map(e => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('rounded px-3 py-1.5 capitalize', estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e || 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <FileText className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin contratos.</p>
            </div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Folio / Nº</th>
                  <th className="px-5 py-3">Proveedor</th>
                  <th className="px-5 py-3">Tipo</th>
                  <th className="px-5 py-3">Vigencia</th>
                  <th className="px-5 py-3 text-right">Monto máx.</th>
                  <th className="px-5 py-3 text-right">Anticipo</th>
                  <th className="px-5 py-3">Estado</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(c => (
                  <tr key={c.id} className="hover:bg-muted/50">
                    <td className="px-5 py-3">
                      <div className="num font-semibold">{c.folio}</div>
                      <div className="num text-[11px] text-ink-mute">{c.numero_contrato}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div>{c.proveedor_nombre}</div>
                      <div className="num text-[11px] text-ink-mute">{c.proveedor_rfc}</div>
                    </td>
                    <td className="px-5 py-3 text-[12px] capitalize">{c.tipo.replace(/_/g, ' ')}</td>
                    <td className="num px-5 py-3 text-[11px] text-ink-mute">{fmt.date(c.fecha_inicio)} → {fmt.date(c.fecha_fin)}</td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(c.monto_maximo)}</td>
                    <td className="num px-5 py-3 text-right text-[11px] text-ink-mute">
                      {c.anticipo_monto > 0 ? `${fmt.money(c.anticipo_monto)} (amort ${fmt.money(c.anticipo_amortizado)})` : '—'}
                    </td>
                    <td className="px-5 py-3"><EstadoPill e={c.estado} /></td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/app/contratos/${c.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <NuevoContratoModal open={openNew} onClose={() => setOpenNew(false)}
        onCreated={() => { setOpenNew(false); qc.invalidateQueries({ queryKey: ['contratos'] }); }} />
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    borrador: 'bg-stone-100 text-ink-mute',
    firmado: 'bg-sky-50 text-sky-700',
    vigente: 'bg-emerald-50 text-emerald-700',
    finiquitado: 'bg-brand-50 text-brand-700',
    rescindido: 'bg-rose-50 text-rose-700',
    cancelado: 'bg-stone-100 text-ink-soft',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', cfg[e] ?? 'bg-muted')}>{e}</span>;
}

function NuevoContratoModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [v, setV] = useState({
    numero_contrato: '', proveedor_id: '',
    tipo: 'adquisicion', modalidad: 'cerrado', objeto: '',
    fecha_inicio: '', fecha_fin: '',
    monto_maximo: 0, anticipo_porcentaje: 0,
    fundamento_legal: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: provs } = useQuery({
    queryKey: ['proveedores-pick'], enabled: open,
    queryFn: () => api<{ items: any[] }>('/proveedores?limit=500'),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api('/contratos', {
        method: 'POST',
        body: {
          ...v,
          fecha_inicio: new Date(v.fecha_inicio).toISOString(),
          fecha_fin: new Date(v.fecha_fin).toISOString(),
          monto_maximo: Number(v.monto_maximo),
          anticipo_porcentaje: Number(v.anticipo_porcentaje),
        },
      });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nuevo contrato" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="ctr-form" loading={subm}>Crear en borrador</Button>
      </>}>
      <form id="ctr-form" onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {err && <div className="sm:col-span-2 rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <Input name="numero" label="Número contrato (oficial)" required
          value={v.numero_contrato} onChange={(e) => setV({ ...v, numero_contrato: e.target.value })} />
        <div>
          <label className="text-[13px] font-medium">Proveedor</label>
          <select required value={v.proveedor_id} onChange={(e) => setV({ ...v, proveedor_id: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="">(selecciona)</option>
            {(provs?.items ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.rfc} — {p.razon_social}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Tipo</label>
          <select value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="adquisicion">Adquisición</option>
            <option value="arrendamiento">Arrendamiento</option>
            <option value="servicio">Servicio</option>
            <option value="obra_publica">Obra pública</option>
            <option value="servicio_relacionado_obra">Servicio relacionado con obra</option>
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Modalidad</label>
          <select value={v.modalidad} onChange={(e) => setV({ ...v, modalidad: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="cerrado">Cerrado</option>
            <option value="abierto">Abierto (min/max)</option>
            <option value="plurianual">Plurianual</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[13px] font-medium">Objeto</label>
          <textarea required rows={2} value={v.objeto} onChange={(e) => setV({ ...v, objeto: e.target.value })}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
        <Input name="ini" label="Fecha inicio" type="date" required
          value={v.fecha_inicio} onChange={(e) => setV({ ...v, fecha_inicio: e.target.value })} />
        <Input name="fin" label="Fecha fin" type="date" required
          value={v.fecha_fin} onChange={(e) => setV({ ...v, fecha_fin: e.target.value })} />
        <Input name="monto" label="Monto máximo" type="number" step="0.01" required
          value={v.monto_maximo} onChange={(e) => setV({ ...v, monto_maximo: parseFloat(e.target.value) || 0 })} />
        <Input name="ant" label="Anticipo %" type="number" step="0.01"
          value={v.anticipo_porcentaje} onChange={(e) => setV({ ...v, anticipo_porcentaje: parseFloat(e.target.value) || 0 })} />
        <Input name="fund" label="Fundamento legal" hint="Art. 42 LAASSP…"
          value={v.fundamento_legal} onChange={(e) => setV({ ...v, fundamento_legal: e.target.value })} />
      </form>
    </Modal>
  );
}

// ============================================================================
// Detalle de contrato
// ============================================================================
export function ContratoDetallePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [openGar, setOpenGar] = useState(false);
  const [openAnexo, setOpenAnexo] = useState(false);

  const { data: c } = useQuery<any>({ queryKey: ['contrato', id], queryFn: () => api(`/contratos/${id}`) });

  async function accion(ep: string, body?: any) {
    try {
      await api(`/contratos/${id}/${ep}`, { method: 'POST', body: body ?? {} });
      qc.invalidateQueries({ queryKey: ['contrato', id] });
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  async function liberarGar(garID: string) {
    if (!confirm('¿Liberar garantía?')) return;
    try {
      await api(`/contratos/${id}/garantias/${garID}/liberar`, { method: 'POST' });
      qc.invalidateQueries({ queryKey: ['contrato', id] });
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  if (!c) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/contratos" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a contratos
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <FileText size={20} />
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700">{c.folio} · {c.numero_contrato}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">{c.proveedor_nombre}</h1>
              <p className="text-[12px] text-ink-mute capitalize">{c.tipo.replace(/_/g, ' ')} · {c.modalidad} · {c.estado}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {c.estado === 'borrador' && <Button onClick={() => accion('firmar')}><PenTool size={14} /> Firmar</Button>}
            {(c.estado === 'firmado' || c.estado === 'vigente') && (
              <>
                <Button variant="secondary" onClick={() => accion('finiquitar')}><CheckCheck size={14} /> Finiquitar</Button>
                <Button variant="ghost" className="text-danger"
                  onClick={() => {
                    const m = prompt('Motivo de rescisión:');
                    if (m) accion('rescindir', { motivo: m });
                  }}><XCircle size={14} /> Rescindir</Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Stat label="Monto máximo" value={fmt.money(c.monto_maximo)} />
        <Stat label="Anticipo" value={fmt.money(c.anticipo_monto)} sub={`${c.anticipo_porcentaje}% · amort ${fmt.money(c.anticipo_amortizado)}`} />
        <Stat label="Vigencia" value={`${fmt.date(c.fecha_inicio)} → ${fmt.date(c.fecha_fin)}`} />
      </div>

      <Card>
        <CardHeader title="Objeto" />
        <CardBody><p className="text-[13px]">{c.objeto}</p></CardBody>
      </Card>

      <Card>
        <CardHeader title="Garantías" action={
          <Button size="sm" variant="ghost" onClick={() => setOpenGar(true)}><Plus size={12} /> agregar</Button>
        } />
        <CardBody className="p-0">
          {(c.garantias ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin garantías.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] uppercase text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Tipo</th>
                  <th className="px-5 py-3">Instrumento</th>
                  <th className="px-5 py-3">Emisor / Póliza</th>
                  <th className="px-5 py-3 text-right">Monto</th>
                  <th className="px-5 py-3">Vencimiento</th>
                  <th className="px-5 py-3">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {c.garantias.map((g: any) => (
                  <tr key={g.id}>
                    <td className="px-5 py-3 capitalize">{g.tipo.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-3 text-[12px] text-ink-mute">{g.instrumento}</td>
                    <td className="px-5 py-3 text-[12px]">{g.emisor ?? '—'} <span className="text-ink-mute">{g.poliza_numero ?? ''}</span></td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(g.monto)}</td>
                    <td className="num px-5 py-3 text-[11px] text-ink-mute">{fmt.date(g.fecha_vencimiento)}</td>
                    <td className="px-5 py-3">
                      <span className={cn('rounded-full px-2 py-0.5 text-[11px]',
                        g.estado === 'vigente' ? 'bg-emerald-50 text-emerald-700' :
                        g.estado === 'liberada' ? 'bg-sky-50 text-sky-700' :
                        'bg-stone-100 text-ink-mute')}>{g.estado}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {g.estado === 'vigente' && <Button size="sm" variant="ghost" onClick={() => liberarGar(g.id)}>Liberar</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Anexos" action={
          <Button size="sm" variant="ghost" onClick={() => setOpenAnexo(true)}><Paperclip size={12} /> agregar</Button>
        } />
        <CardBody className="p-0">
          {(c.anexos ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin anexos.</div>
          ) : (
            <ul className="divide-y divide-border">
              {c.anexos.map((a: any) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-3 text-[13px]">
                  <div>
                    <div>{a.nombre}</div>
                    <div className="text-[11px] text-ink-mute capitalize">{a.tipo} · {fmt.date(a.fecha)}</div>
                  </div>
                  {a.documento_url && <a href={a.documento_url} target="_blank" rel="noreferrer" className="text-[12px] text-brand-600">ver documento →</a>}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <GarantiaModal contratoID={id} open={openGar} onClose={() => setOpenGar(false)}
        onDone={() => { setOpenGar(false); qc.invalidateQueries({ queryKey: ['contrato', id] }); }} />
      <AnexoModal contratoID={id} open={openAnexo} onClose={() => setOpenAnexo(false)}
        onDone={() => { setOpenAnexo(false); qc.invalidateQueries({ queryKey: ['contrato', id] }); }} />
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[18px] font-semibold text-ink">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-ink-mute">{sub}</div>}
    </div>
  );
}

function GarantiaModal({ contratoID, open, onClose, onDone }: any) {
  const [v, setV] = useState({
    tipo: 'cumplimiento', instrumento: 'fianza',
    emisor: '', poliza_numero: '', porcentaje: 10, monto: 0,
    fecha_emision: '', fecha_vencimiento: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api(`/contratos/${contratoID}/garantias`, {
        method: 'POST',
        body: {
          ...v,
          monto: Number(v.monto),
          porcentaje: Number(v.porcentaje),
          fecha_emision: new Date(v.fecha_emision).toISOString(),
          fecha_vencimiento: new Date(v.fecha_vencimiento).toISOString(),
        },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Agregar garantía"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="gar-form" loading={subm}><Shield size={14} /> Guardar</Button>
      </>}>
      <form id="gar-form" onSubmit={submit} className="grid grid-cols-2 gap-3">
        {err && <div className="col-span-2 rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div>
          <label className="text-[13px] font-medium">Tipo</label>
          <select value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="cumplimiento">Cumplimiento</option>
            <option value="anticipo">Anticipo</option>
            <option value="vicios_ocultos">Vicios ocultos</option>
            <option value="responsabilidad_civil">Responsabilidad civil</option>
            <option value="calidad">Calidad</option>
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Instrumento</label>
          <select value={v.instrumento} onChange={(e) => setV({ ...v, instrumento: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="fianza">Fianza</option>
            <option value="cheque_certificado">Cheque certificado</option>
            <option value="billete_deposito">Billete de depósito</option>
            <option value="carta_credito">Carta crédito</option>
            <option value="retencion">Retención</option>
          </select>
        </div>
        <Input name="emisor" label="Emisor (afianzadora)" value={v.emisor} onChange={(e) => setV({ ...v, emisor: e.target.value })} />
        <Input name="pol" label="Nº póliza" value={v.poliza_numero} onChange={(e) => setV({ ...v, poliza_numero: e.target.value })} />
        <Input name="pct" label="%" type="number" step="0.01" value={v.porcentaje} onChange={(e) => setV({ ...v, porcentaje: parseFloat(e.target.value) || 0 })} />
        <Input name="monto" label="Monto" type="number" step="0.01" required value={v.monto} onChange={(e) => setV({ ...v, monto: parseFloat(e.target.value) || 0 })} />
        <Input name="emi" label="Fecha emisión" type="date" required value={v.fecha_emision} onChange={(e) => setV({ ...v, fecha_emision: e.target.value })} />
        <Input name="venc" label="Fecha vencimiento" type="date" required value={v.fecha_vencimiento} onChange={(e) => setV({ ...v, fecha_vencimiento: e.target.value })} />
      </form>
    </Modal>
  );
}

function AnexoModal({ contratoID, open, onClose, onDone }: any) {
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('tecnico');
  const [url, setUrl] = useState('');
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setSubm(true);
    try {
      await api(`/contratos/${contratoID}/anexos`, { method: 'POST', body: { nombre, tipo, documento_url: url || undefined } });
      onDone();
    } catch (x) { alert(x instanceof ApiError ? x.message : 'Error'); }
    finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Agregar anexo"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="anexo-form" loading={subm}>Agregar</Button>
      </>}>
      <form id="anexo-form" onSubmit={submit} className="flex flex-col gap-3">
        <Input name="nombre" label="Nombre" required value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <div>
          <label className="text-[13px] font-medium">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="tecnico">Técnico</option>
            <option value="economico">Económico</option>
            <option value="legal">Legal</option>
            <option value="convenio_modificatorio">Convenio modificatorio</option>
            <option value="otro">Otro</option>
          </select>
        </div>
        <Input name="url" label="URL del documento (opcional)" value={url} onChange={(e) => setUrl(e.target.value)} />
      </form>
    </Modal>
  );
}
